import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface CallBucketRow {
  date: string;        // YYYY-MM-DD senior local
  ampm_count: number;
  pmevening_count: number;
}

/**
 * F02 — fetches the last 7 days of call_buckets for a senior. RLS guarantees
 * caregivers only see their own seniors' rows.
 *
 * Polls on a 5-min interval — the underlying data only changes at 15:00 and
 * 22:00 local, so anything more frequent is wasted. Caller can also bump the
 * key via [refreshKey] to force an immediate refetch.
 */
export function useCallBuckets(seniorId: string | undefined, refreshKey = 0) {
  const [buckets, setBuckets] = useState<CallBucketRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!seniorId) {
      setBuckets([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const fetchOnce = async () => {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const fromDate = sevenDaysAgo.toISOString().slice(0, 10);

      const { data, error: e } = await supabase
        .from('call_buckets')
        .select('date, ampm_count, pmevening_count')
        .eq('senior_user_id', seniorId)
        .gte('date', fromDate)
        .order('date', { ascending: true });

      if (cancelled) return;
      if (e) {
        console.warn('[useCallBuckets] fetch error:', e.message);
        setError(e.message);
        setBuckets([]);
      } else {
        setError(null);
        setBuckets((data ?? []) as CallBucketRow[]);
      }
      setLoading(false);
    };

    setLoading(true);
    fetchOnce();
    const t = setInterval(fetchOnce, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [seniorId, refreshKey]);

  return { buckets, loading, error };
}
